/**
 * Rate Limiting Service
 * Prevents abuse by limiting requests per IP/user
 */

export interface RateLimitConfig {
    windowMs: number; // Time window in milliseconds
    maxRequests: number; // Max requests per window
    blockDuration?: number; // How long to block after limit exceeded (ms)
}

export interface RateLimitInfo {
    ip: string;
    requests: number;
    windowStart: number;
    blocked: boolean;
    blockUntil?: number;
}

class RateLimitingService {
    private static limits: Map<string, RateLimitInfo> = new Map();

    // Default rate limit configurations
    private static readonly CONFIGS: Record<string, RateLimitConfig> = {
        global: {
            windowMs: 60000, // 1 minute
            maxRequests: 100,
            blockDuration: 300000 // 5 minutes
        },
        login: {
            windowMs: 300000, // 5 minutes
            maxRequests: 5,
            blockDuration: 900000 // 15 minutes
        },
        api: {
            windowMs: 60000, // 1 minute
            maxRequests: 60,
            blockDuration: 180000 // 3 minutes
        },
        admin: {
            windowMs: 60000, // 1 minute
            maxRequests: 30,
            blockDuration: 600000 // 10 minutes
        }
    };

    /**
     * Check if a request should be allowed
     */
    static checkLimit(ip: string, endpoint: string = 'global'): {
        allowed: boolean;
        remaining: number;
        resetTime: number;
        message?: string;
    } {
        const config = this.getConfig(endpoint);
        const now = Date.now();
        const key = `${ip}:${endpoint}`;

        let limitInfo = this.limits.get(key);

        // Check if IP is currently blocked
        if (limitInfo?.blocked && limitInfo.blockUntil && now < limitInfo.blockUntil) {
            const remainingTime = Math.ceil((limitInfo.blockUntil - now) / 1000);
            return {
                allowed: false,
                remaining: 0,
                resetTime: limitInfo.blockUntil,
                message: `Too many requests. Blocked for ${remainingTime} seconds.`
            };
        }

        // Initialize or reset window
        if (!limitInfo || now - limitInfo.windowStart > config.windowMs) {
            limitInfo = {
                ip,
                requests: 0,
                windowStart: now,
                blocked: false
            };
            this.limits.set(key, limitInfo);
        }

        // Increment request count
        limitInfo.requests++;

        // Check if limit exceeded
        if (limitInfo.requests > config.maxRequests) {
            limitInfo.blocked = true;
            limitInfo.blockUntil = now + (config.blockDuration || config.windowMs);
            
            console.warn(`🚫 Rate limit exceeded for ${ip} on ${endpoint}`);
            
            return {
                allowed: false,
                remaining: 0,
                resetTime: limitInfo.blockUntil,
                message: `Rate limit exceeded. Try again later.`
            };
        }

        const remaining = config.maxRequests - limitInfo.requests;
        const resetTime = limitInfo.windowStart + config.windowMs;

        return {
            allowed: true,
            remaining,
            resetTime
        };
    }

    /**
     * Get rate limit configuration for an endpoint
     */
    private static getConfig(endpoint: string): RateLimitConfig {
        // Match endpoint to config
        if (endpoint.includes('login') || endpoint.includes('auth')) {
            return this.CONFIGS.login;
        }
        if (endpoint.includes('admin')) {
            return this.CONFIGS.admin;
        }
        if (endpoint.includes('api')) {
            return this.CONFIGS.api;
        }
        return this.CONFIGS.global;
    }

    /**
     * Manually block an IP
     */
    static blockIP(ip: string, durationMs: number = 3600000): void {
        const now = Date.now();
        const key = `${ip}:global`;
        
        this.limits.set(key, {
            ip,
            requests: 999,
            windowStart: now,
            blocked: true,
            blockUntil: now + durationMs
        });

        console.log(`🚫 Manually blocked IP ${ip} for ${durationMs / 1000} seconds`);
    }

    /**
     * Unblock an IP
     */
    static unblockIP(ip: string): void {
        // Remove all limits for this IP
        for (const key of this.limits.keys()) {
            if (key.startsWith(`${ip}:`)) {
                this.limits.delete(key);
            }
        }
        console.log(`✅ Unblocked IP ${ip}`);
    }

    /**
     * Check if an IP is blocked
     */
    static isBlocked(ip: string): boolean {
        const now = Date.now();
        
        for (const [key, limitInfo] of this.limits.entries()) {
            if (key.startsWith(`${ip}:`) && limitInfo.blocked) {
                if (limitInfo.blockUntil && now < limitInfo.blockUntil) {
                    return true;
                }
            }
        }
        
        return false;
    }

    /**
     * Get limit info for an IP
     */
    static getLimitInfo(ip: string, endpoint: string = 'global'): RateLimitInfo | null {
        const key = `${ip}:${endpoint}`;
        return this.limits.get(key) || null;
    }

    /**
     * Get all rate limit info
     */
    static getAllLimits(): RateLimitInfo[] {
        return Array.from(this.limits.values());
    }

    /**
     * Get blocked IPs
     */
    static getBlockedIPs(): string[] {
        const now = Date.now();
        const blocked = new Set<string>();

        for (const [key, limitInfo] of this.limits.entries()) {
            if (limitInfo.blocked && limitInfo.blockUntil && now < limitInfo.blockUntil) {
                blocked.add(limitInfo.ip);
            }
        }

        return Array.from(blocked);
    }

    /**
     * Get statistics
     */
    static getStatistics() {
        const now = Date.now();
        const stats = {
            totalIPs: new Set<string>(),
            blockedIPs: 0,
            totalRequests: 0,
            byEndpoint: {} as Record<string, number>
        };

        for (const [key, limitInfo] of this.limits.entries()) {
            stats.totalIPs.add(limitInfo.ip);
            stats.totalRequests += limitInfo.requests;

            const endpoint = key.split(':')[1] || 'unknown';
            stats.byEndpoint[endpoint] = (stats.byEndpoint[endpoint] || 0) + limitInfo.requests;

            if (limitInfo.blocked && limitInfo.blockUntil && now < limitInfo.blockUntil) {
                stats.blockedIPs++;
            }
        }

        return {
            ...stats,
            totalIPs: stats.totalIPs.size
        };
    }

    /**
     * Cleanup expired entries
     */
    static cleanup(): void {
        const now = Date.now();
        let cleaned = 0;

        for (const [key, limitInfo] of this.limits.entries()) {
            const config = this.getConfig(key.split(':')[1] || 'global');
            const isExpired = now - limitInfo.windowStart > config.windowMs * 2;
            const isUnblocked = limitInfo.blocked && limitInfo.blockUntil && now > limitInfo.blockUntil;

            if (isExpired || isUnblocked) {
                this.limits.delete(key);
                cleaned++;
            }
        }

        if (cleaned > 0) {
            console.log(`🧹 Cleaned up ${cleaned} expired rate limit entries`);
        }
    }

    /**
     * Update rate limit configuration
     */
    static updateConfig(endpoint: string, config: Partial<RateLimitConfig>): void {
        if (this.CONFIGS[endpoint]) {
            this.CONFIGS[endpoint] = {
                ...this.CONFIGS[endpoint],
                ...config
            };
            console.log(`✅ Updated rate limit config for ${endpoint}`);
        }
    }

    /**
     * Reset all limits
     */
    static reset(): void {
        this.limits.clear();
        console.log('🔄 Reset rate limiting service');
    }
}

export default RateLimitingService;
